import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Passwordless login for Linux',
    description:
      'Sign in to servers and workstations with your Monosign identity: a push approval on your phone instead of a local password nobody rotates.',
    bullets: [
      'Push-approval login backed by Monosign identity',
      'No local password sprawl across the fleet',
      'Every session tied to a central identity',
      'Native PAM (pluggable authentication modules) integration',
    ],
  },
  {
    title: 'Least privilege for sudo',
    description:
      'Replace all-or-nothing sudoers files with command-level elevation policy. The command that needs root gets it, with a record.',
    bullets: [
      'Command-level elevation policy, not blanket sudoers',
      'Approval and justification flows for sensitive commands',
      'Every elevation audited centrally',
      'No more untracked root-equivalent accounts',
    ],
  },
  {
    title: 'One agent, not an agent zoo',
    description:
      'A single lightweight daemon with native PAM and sudo integration. Turn on the capabilities each host needs: login, EPM, or both.',
    bullets: [
      'Single lightweight daemon per host',
      'Capabilities toggled per host: login, EPM, or both',
      'Minimal footprint on the system',
      'Secure loopback IPC, hardened by design',
    ],
  },
  {
    title: 'Part of the platform',
    description:
      'Linux joins the same privileged-access platform as everything else: the same policies, the same approvals, the same audit trail.',
    bullets: [
      'Same policy model as Windows EPM, Kubernetes, and databases',
      'Identities come from Monosign',
      'Approvals in the workflows your team already uses',
      'Evidence lands in the same reports',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Install the agent',
    description:
      'Roll the lightweight agent out to RHEL, Ubuntu, and SLES hosts; it registers with the platform in minutes.',
  },
  {
    title: 'Choose capabilities',
    description:
      'Enable passwordless login, sudo least privilege, or both, per host or per group, from one console.',
  },
  {
    title: 'Enforce and audit',
    description:
      'Logins go through Monosign, elevations follow policy, and every decision lands in the central audit trail.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'EPM for Windows',
    description:
      'The same model on the desktop fleet.',
    href: '/pam/epm',
  },
  {
    label: 'AD bridging',
    description:
      'Join the same Linux estate to Active Directory.',
    href: '/pam/ad-bridging',
  },
  {
    label: 'DevOps privileged access',
    description:
      'Privileged access for the pipelines and clusters around your hosts.',
    href: '/solutions/devops-privileged-access',
  },
]

export default function LinuxAgentPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Linux identity agent: passwordless login & least privilege"
        description="One lightweight agent brings Linux into the identity platform: passwordless login with Monosign push approval, command-level sudo policy with approval flows, native PAM/sudo integration, and central audit, on RHEL, Ubuntu, and SLES."
        path="/pam/linux"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Linux Identity Agent"
          title={
            <>
              Linux logs in like it&apos;s 1999.
              <br />
              Give it modern identity.
            </>
          }
          body="Linux fleets still authenticate with local passwords and SSH keys nobody tracks, and sudo rights are all-or-nothing. One lightweight agent brings Linux into the identity platform: passwordless login through Monosign, command-level sudo policy through Monopam, toggled per host."
          badges={[
            'Passwordless login via Monosign',
            'Command-level sudo policy',
            'RHEL · Ubuntu · SLES',
          ]}
        />
        <SolutionPillars
          title={<>Two capabilities, one agent.</>}
          body="Passwordless login and endpoint privilege management ship in a single daemon with native PAM and sudo integration; switch on what each host needs, and govern it from the same console as the rest of your estate."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From local passwords to platform identity.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
